/**
 * Groups surveys into the per-BU sections the list screen renders, and into the feature each one
 * asks about inside them.
 *
 * The section summary counts **surveys**, never responses: two surveys measure different things
 * on different scales, and a pooled answer count across them is a number that looks like
 * information and is not (R21). The feature level counts surveys for the same reason.
 *
 * **Why there is a second level at all.** With the exit questionnaire, two cards start talking
 * about the same opt-in feature with nothing on screen saying so, and the first reaction is "por
 * que tem duas pesquisas do menu?". They are not a part of one another: they are two moments of
 * asking about the same thing — whoever is *using* it answers the enquete days after turning it on,
 * whoever is *leaving* answers at the instant they turn it off. Grouping by the feature answers
 * that before anyone has to form the question.
 *
 * The unlinked bucket is last and is **not** a special case: in most tenants it is the biggest
 * group, and the caller renders it without a heading when it is the only one.
 *
 * @module Pages/BUs/Surveys
 */

import type { Survey } from '../Types/Surveys.types';
import { compareTenantSlugs } from '@/Shared/brand/tenantOrder';

/** The surveys that ask about one opt-in feature — or the ones that ask about none. */
export interface FeatureSurveyGroup {
  /** `null` in the bucket of surveys with no `requiresOptInFeature`. */
  featureId: string | null;
  featureName: string | null;
  featureKey: string | null;
  surveys: Survey[];
  /** e.g. "2 pesquisas". */
  countLabel: string;
}

/** One BU's section of surveys. */
export interface TenantSurveyGroup {
  tenantSlug: string;
  /** Every survey of the BU, flat and ordered by slug. */
  surveys: Survey[];
  /** The same surveys, split by the feature they ask about. The unlinked bucket comes last. */
  features: FeatureSurveyGroup[];
  /** e.g. "3 pesquisas". */
  countLabel: string;
  /** e.g. "2 no ar". */
  liveLabel: string;
}

const isLive = (survey: Survey, now: Date): boolean => {
  // A deleted survey is delivered to nobody, whatever its approval says.
  if (survey.deletedAt) return false;
  if (!survey.isActive || survey.approvalStatus !== 'APPROVED') return false;
  if (survey.opensAt && new Date(survey.opensAt) > now) return false;
  if (survey.closesAt && new Date(survey.closesAt) < now) return false;

  return true;
};

const countLabelOf = (surveys: Survey[]): string =>
  `${surveys.length} ${surveys.length === 1 ? 'pesquisa' : 'pesquisas'}`;

/**
 * Splits one BU's surveys by the feature they hang off.
 *
 * Ordering inside a feature is by slug, like the flat list, so approving a survey does not make the
 * cards jump. Features are ordered by name and the unlinked bucket is pinned last, wherever its
 * size puts it.
 */
const groupByFeature = (surveys: Survey[]): FeatureSurveyGroup[] => {
  const byFeature = new Map<string, Survey[]>();
  const unlinked: Survey[] = [];

  for (const survey of surveys) {
    const feature = survey.requiresOptInFeature;

    if (!feature) {
      unlinked.push(survey);
      continue;
    }

    const existing = byFeature.get(feature.id);

    if (existing) {
      existing.push(survey);
    } else {
      byFeature.set(feature.id, [survey]);
    }
  }

  const groups: FeatureSurveyGroup[] = [...byFeature.values()]
    .map(featureSurveys => {
      const ordered = [...featureSurveys].sort((a, b) => a.slug.localeCompare(b.slug));
      const feature = ordered[0].requiresOptInFeature;

      return {
        featureId: feature?.id ?? null,
        featureName: feature?.name ?? null,
        featureKey: feature?.key ?? null,
        surveys: ordered,
        countLabel: countLabelOf(ordered),
      };
    })
    .sort((a, b) => (a.featureName ?? '').localeCompare(b.featureName ?? ''));

  if (unlinked.length > 0) {
    const ordered = [...unlinked].sort((a, b) => a.slug.localeCompare(b.slug));

    groups.push({
      featureId: null,
      featureName: null,
      featureKey: null,
      surveys: ordered,
      countLabel: countLabelOf(ordered),
    });
  }

  return groups;
};

/**
 * Groups by tenant slug, tenants in the house order and surveys inside each one by slug, so the
 * list does not reshuffle when a survey is approved.
 */
export function groupSurveysByTenant(surveys: Survey[], now = new Date()): TenantSurveyGroup[] {
  const byTenant = new Map<string, Survey[]>();

  for (const survey of surveys) {
    const existing = byTenant.get(survey.tenantSlug);

    if (existing) {
      existing.push(survey);
    } else {
      byTenant.set(survey.tenantSlug, [survey]);
    }
  }

  return [...byTenant.entries()]
    .sort(([a], [b]) => compareTenantSlugs(a, b))
    .map(([tenantSlug, tenantSurveys]) => {
      const ordered = [...tenantSurveys].sort((a, b) => a.slug.localeCompare(b.slug));
      const liveCount = ordered.filter(survey => isLive(survey, now)).length;
      const deletedCount = ordered.filter(survey => survey.deletedAt).length;

      return {
        tenantSlug,
        surveys: ordered,
        features: groupByFeature(ordered),
        countLabel: countLabelOf(ordered),
        /*
          The deleted ones are counted apart rather than folded into "no ar": they are in this
          list only because they are holding a slug, and adding them to a count of what is
          running would be the same lie the screen is here to stop telling.
        */
        liveLabel:
          deletedCount > 0
            ? `${liveCount} no ar · ${deletedCount} excluída${deletedCount === 1 ? '' : 's'}`
            : `${liveCount} no ar`,
      };
    });
}
